import { useContext, useRef, useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { matchSorter } from 'match-sorter'
import { t } from 'ttag'

import { PB97T, PB40T, PB90T, PB, PG, SLB, SO, SG, SY, SPK } from '@h2/components/colors'
import { lists } from '@h2/components/mixins'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Highlighter } from '@h2/components/display/highlighter'
import { IWantToIcon } from '@h2/components/icons/iWantTo'
import { features } from '@h2/components/media'

import { RecentActivitiesPopular } from './recentActivitiesPopular'
import { Categories } from './categories'
import { Popular } from './popular'
import { Panel } from './panel'

import { Input } from '../search'
import { Context } from '../../context'
import { ViewSwitcher } from '../../viewSwitcher'
import { Link as LinkComponent } from '../../../link'
import { useCallbackOnEventOutside } from '../../../hooks/useCallbackOnEventOutside'

const Wrap = styled.div`
  position: fixed;
  top: ${({ switcherProfileHeight }) => switcherProfileHeight};
  left: 0;
  bottom: ${({ navHeight }) => navHeight};
  width: 100%;
  background-color: ${PB97T};
  z-index: 1;

  @media (${features.minWidth.laptop}) {
    top: ${({ navHeight }) => navHeight};
    bottom: 0;
    overflow: hidden;
    height: ${({ isActive }) => (isActive ? '100%' : 0)};
    transition: height 0.4s ease;
  }
`
const FlexWrap = styled(Flex)`
  height: 100%;

  form {
    label {
      display: block;
    }
  }
`
const PanelTitle = styled(Panel)`
  display: none;

  @media (${features.minWidth.laptop}) {
    display: block;
    padding: 10px 0;
    border-top: 2px solid ${PG};
    border-bottom: 8px solid ${PB97T};

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      > h3 {
        display: flex;
        align-items: center;

        span[role='img'] {
          margin-right: 10px;
        }
      }
    }
  }
`
const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
`
const FlexContent = styled(Flex)`
  height: 100%;

  @media (${features.minWidth.laptop}) {
    flex-direction: row;
    height: calc(
      100vh - ${({ titleHeight }) => titleHeight} - ${({ navHeight }) => navHeight}
    );
  }
`
const FlexContentWrap = styled(Flex)`
  height: calc(100% - ${({ popularHeight }) => popularHeight});

  @media (${features.minWidth.laptop}) {
    height: 100%;
    width: 80%;
    padding: 0 30px;
  }
`
const BoxContentTop = styled(Box)`
  @media (${features.minWidth.laptop}) {
    padding: 10px 0 0 0;
  }
`
const FlexSwitchView = styled(Flex)`
  display: none;

  @media (${features.minWidth.laptop}) {
    display: flex;
  }
`
const PanelSearch = styled(Panel)`
  @media (${features.minWidth.laptop}) {
    flex: 0 1 100%;
    margin: 20px 0;
    background-color: transparent;

    > div:first-child {
      padding: 0;
    }
  }
`
const FlexSearch = styled(Flex)`
  > div {
    position: relative;
    width: 100%;
  }
`
const SearchInput = styled(Input)`
  padding: 15px 35px;

  @media (${features.minWidth.laptop}) {
    padding: 12px 20px;
    border: 1px solid ${PB90T};
    border-radius: 8px;
  }
`
const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 15px;
  left: 0;

  @media (${features.minWidth.laptop}) {
    display: none;
  }
`
const FlexContentInner = styled(Flex)`
  height: calc(100% - ${({ topHeight }) => topHeight});

  @media (${features.minWidth.laptop}) {
    flex-direction: row;
  }
`
const BoxCategories = styled(Box)`
  @media (${features.minWidth.laptop}) {
    width: 30%;
    min-width: 320px;
    height: 100%;
    overflow-y: auto;
  }
`
const PanelItems = styled(Panel)`
  background-color: white;
  height: 100%;
  overflow-y: auto;

  > div {
    padding: 0;
  }

  @media (${features.minWidth.laptop}) {
    background-color: transparent;
    width: 70%;
  }
`
const Section = styled.section`
  padding: 15px 15px 0 15px;
  background-color: white;

  &:last-child {
    margin-bottom: 75vh;
  }

  &:only-of-type {
    margin-bottom: 0;
  }

  @media (${features.minWidth.laptop}) {
    padding: 0;
    border-style: solid;
    border-color: white;
    border-width: 0;
    border-left-width: 2px;
    border-top-width: 2px;
    border-radius: 4px;

    &:nth-child(n + 2) {
      margin-top: 20px;
    }

    &.active,
    &:first-child {
      border-top: none;
    }

    &.management {
      border-top-color: ${SY};

      &.active,
      &:first-child {
        border-left-color: ${SY};
      }
    }

    &.activities {
      border-top-color: ${SLB};

      &.active,
      &:first-child {
        border-left-color: ${SLB};
      }
    }

    &.research {
      border-top-color: ${SO};

      &.active,
      &:first-child {
        border-left-color: ${SO};
      }
    }

    &.my-stuff {
      border-top-color: ${SG};

      &.active,
      &:first-child {
        border-left-color: ${SG};
      }
    }

    &.maintenance {
      border-top-color: ${SPK};

      &.active,
      &:first-child {
        border-left-color: ${SPK};
      }
    }

    > h3 {
      padding: 15px 0 0 15px;
      margin: 0 0 10px 0;
    }
  }
`
const Link = styled(LinkComponent)`
  display: flex;
  flex-direction: column;
  margin-left: 10px;
  width: 100%;

  > h5 {
    margin: 0;
    margin-bottom: 3px;
    letter-spacing: 0.4px;
  }

  &:hover {
    text-decoration: none;
  }
`
const ItemList = styled.ul`
  ${lists};

  > li {
    display: flex;
    position: relative;
    align-items: start;
    margin-bottom: 20px;

    > span:first-child {
      flex: 0 0 20px;
    }

    > span[role='img'] {
      svg {
        width: 20px;
        height: 20px;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }

    &.loading {
      flex-direction: column;

      > span:first-child {
        margin-bottom: 3px;
      }

      > span:nth-child(n + 2) {
        margin-top: 3px;
      }
    }

    a[href]::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
    }

    @media (${features.minWidth.laptop}) {
      margin: 0;
      padding: 10px 15px;
      background-repeat: no-repeat;
      background-image: linear-gradient(to right, ${PB97T}, ${PB97T});
      background-size: 0;
      transition: background-size 0.25s ease-in-out;

      &:hover {
        background-size: 100%;
      }
    }
  }
`
const PanelPopular = styled(Panel)`
  padding: 15px 0;
  background-color: ${PB97T};

  @media (${features.minWidth.laptop}) {
    display: none;
  }
`
const matchSorterOptions = {
  sorter: items => items,
  keys: [({ title }) => title, ({ description }) => description],
  threshold: matchSorter.rankings.CONTAINS
}
const getRandomNum = (min, max) => Math.round(Math.random() * (max - min) + min)
const getCategoryId = title => title.replace(' ', '-').toLowerCase()
const IWantTo = ({ onClose, isActive }) => {
  const wrapRef = useRef()
  const itemsRef = useRef()
  const contentTopRef = useRef()
  const {
    userMenu,
    useReactRouter,
    navHeight,
    switcherProfileHeight,
    handleOnViewChange,
    isMenuFetching,
    isLoggedIn,
    isMobile
  } = useContext(Context)
  const [icons, setIcons] = useState(null)
  const [query, setQuery] = useState('')
  const [filteredMenu, setFilteredMenu] = useState(userMenu?.menu ?? [])
  const [heights, setHeights] = useState(
    isMobile
      ? { title: 0, popular: '104px', contentTop: '50px' }
      : { title: '58px', popular: 0, contentTop: '135px' }
  )
  const handleOnSearch = useCallback(evt => {
    evt.preventDefault()
  }, [])
  const flatMenu = useMemo(() => {
    const flat = []

    if (userMenu) {
      let menu = userMenu.menu

      if (isMobile && isLoggedIn) {
        menu = userMenu.menu.concat([
          {
            title: 'Recent Activity',
            description: 'Your recent activity',
            items: userMenu.recentFeatures
          }
        ])
      }

      menu.forEach(({ title, items, description }) => {
        flat.push(
          items.map(item => ({ ...item, category: title, categoryDesc: description }))
        )
      })

      setFilteredMenu(menu)
    }

    return flat.flat()
  }, [userMenu, isLoggedIn, isMobile])
  const handleOnInput = useMemo(() => {
    return debounce(
      ({ target: { value } }) => {
        const matches = matchSorter(flatMenu, value, matchSorterOptions)
        const menu = []

        if (matches.length) {
          let category = {}

          for (const item of matches) {
            if (item.category === category.title) {
              category.items.push(item)
            } else {
              category = {
                title: item.category,
                description: item.categoryDesc,
                items: [item]
              }
              menu.push(category)
            }
          }
        }

        setQuery(value)
        setFilteredMenu(menu)

        if (itemsRef.current?.scrollTop) {
          itemsRef.current.scrollTop = 0
        }
      },
      150,
      { leading: true }
    )
  }, [flatMenu, itemsRef])
  const categories = useMemo(
    () => filteredMenu.map(({ title, description }) => ({ title, description })),
    [filteredMenu]
  )

  useCallbackOnEventOutside(
    useMemo(() => [wrapRef], [wrapRef]),
    useCallback(
      evt => {
        const profile = document.getElementById('profile-wrap')

        if (isMobile && profile?.contains(evt.target)) {
          onClose(evt)
        }
      },
      [onClose, isMobile]
    )
  )

  useEffect(() => {
    const onKeyUp = evt => {
      if (evt.key === 'Escape') {
        onClose(evt)
      }
    }

    document.addEventListener('keyup', onKeyUp)

    return () => document.removeEventListener('keyup', onKeyUp)
  }, [onClose])

  useEffect(() => {
    if (wrapRef.current && contentTopRef.current) {
      const wrapNode = wrapRef.current
      const contentTop = contentTopRef.current.getBoundingClientRect().height
      const title = wrapNode.querySelector('#iwt-title').getBoundingClientRect().height
      const popular = wrapNode
        .querySelector('#iwt-popular')
        .getBoundingClientRect().height

      setHeights({
        title: `${title}px`,
        popular: `${popular}px`,
        contentTop: `${contentTop}px`
      })
    }
  }, [isMenuFetching])

  useEffect(() => {
    const importIcons = async () => {
      const module = await import(/* webpackChunkName: "iwt-icons" */ './icons')

      if (wrapRef.current) {
        setIcons(module.icons)
      }
    }

    if (!icons) {
      importIcons()
    }
  }, [icons])

  const searchLabel = isMobile
    ? t`Start typing here to filter results...`
    : t`Select a feature below or start typing here to filter the results...`

  return (
    <Wrap
      ref={wrapRef}
      isActive={isActive}
      navHeight={navHeight}
      switcherProfileHeight={switcherProfileHeight}>
      {isActive && (
        <FlexWrap direction="column">
          <PanelTitle id="iwt-title">
            <header>
              <Heading mb="0" level="3">
                <IWantToIcon />
                <span>{t`I want to...`}</span>
              </Heading>
              <CloseIcon icon="times" size="2x" color={PB} onClick={onClose} />
            </header>
          </PanelTitle>
          <FlexContent
            direction="column"
            titleHeight={heights.title}
            navHeight={navHeight}>
            <RecentActivitiesPopular icons={icons} />
            <FlexContentWrap direction="column" popularHeight={heights.popular}>
              <BoxContentTop ref={contentTopRef}>
                <FlexSwitchView justifyContent="space-between" alignItems="center">
                  <Heading level="4" mb="0">
                    {t`What do you want to do?`}
                  </Heading>
                  <Box>
                    <ViewSwitcher onViewChange={handleOnViewChange} />
                  </Box>
                </FlexSwitchView>
                <PanelSearch>
                  <FlexSearch>
                    <Box>
                      <SearchIcon icon="search" size="2x" color={PB40T} />
                      <form onSubmit={handleOnSearch}>
                        <label>
                          <span className="sr-only">{searchLabel}</span>
                          <SearchInput
                            onInput={handleOnInput}
                            placeholder={searchLabel}
                          />
                        </label>
                      </form>
                    </Box>
                  </FlexSearch>
                </PanelSearch>
              </BoxContentTop>
              <FlexContentInner direction="column" topHeight={heights.contentTop}>
                <BoxCategories>
                  <Categories categories={categories} itemsRef={itemsRef} />
                </BoxCategories>
                <PanelItems ref={itemsRef}>
                  {isMenuFetching
                    ? Array(2)
                        .fill()
                        .map((outer, idx) => (
                          <Section key={idx}>
                            <ItemList key={idx}>
                              {Array(5)
                                .fill()
                                .map((inner, jdx) => (
                                  <li key={jdx} className="loading">
                                    <Skeleton
                                      height="16px"
                                      width={`${getRandomNum(25, 55)}%`}
                                    />
                                    <Skeleton
                                      height="10px"
                                      width={`${getRandomNum(95, 99)}%`}
                                    />
                                    <Skeleton
                                      height="10px"
                                      width={`${getRandomNum(0, 85)}%`}
                                    />
                                  </li>
                                ))}
                            </ItemList>
                          </Section>
                        ))
                    : filteredMenu.map((category, idx) => (
                        <Section
                          key={category.title}
                          className={getCategoryId(category.title)}
                          id={getCategoryId(category.title)}>
                          <Heading
                            level="3"
                            aria-live="polite"
                            role="status"
                            id={`section${idx}`}>
                            {category.title}{' '}
                            <Text as="span" size="small">
                              ({t`${category.items.length} items`})
                            </Text>
                          </Heading>
                          <nav aria-labelledby={`section${idx}`}>
                            <ItemList>
                              {category.items.map(
                                ({ useRouter, url, title, description, sectionId }) => {
                                  const Icon = icons?.[sectionId]

                                  return (
                                    <li key={url}>
                                      {Icon ? (
                                        <Icon />
                                      ) : (
                                        <Skeleton width="20px" height="20px" />
                                      )}
                                      <div>
                                        <Link
                                          ssr={!useRouter || !useReactRouter}
                                          href={url}>
                                          <Heading level="5" variant="font">
                                            <Highlighter text={title} mark={query} />
                                          </Heading>
                                        </Link>
                                        {description && (
                                          <Text
                                            as="span"
                                            size="medium"
                                            style={{ marginLeft: '10px' }}>
                                            <Highlighter
                                              text={description}
                                              mark={query}
                                            />
                                          </Text>
                                        )}
                                      </div>
                                    </li>
                                  )
                                }
                              )}
                            </ItemList>
                          </nav>
                        </Section>
                      ))}
                </PanelItems>
              </FlexContentInner>
            </FlexContentWrap>
            <PanelPopular id="iwt-popular">
              <Popular />
            </PanelPopular>
          </FlexContent>
        </FlexWrap>
      )}
    </Wrap>
  )
}

IWantTo.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export { IWantTo }
